import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import LanguageContext from '../../../context/LanguageContext.js';
import { fetchFaqs } from '../../../api/faqApi.js';
import { faqsPlaceholder } from '../../../data/faqsPlaceholder.js';
import { pickLocalized } from '../../../utils/localizedContent.js';
import './Faqs.css';

const PlusIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <path d="M12 5v14M5 12h14" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const Questions = () => {
    const { dir } = useContext(LanguageContext);
    const { t, i18n } = useTranslation();
    const languageKey = i18n.language.split('-')[0];
    const [faqs, setFaqs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [activeIndex, setActiveIndex] = useState(null);

    useEffect(() => {
        let cancelled = false;

        (async () => {
            try {
                setLoading(true);
                setError(false);
                const data = await fetchFaqs();
                if (!cancelled) {
                    const fromApi = Array.isArray(data) ? data : [];
                    setFaqs(fromApi.length > 0 ? fromApi : faqsPlaceholder);
                }
            } catch {
                if (!cancelled) {
                    setFaqs(faqsPlaceholder);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, []);

    const toggleItem = (index) => {
        setActiveIndex(activeIndex === index ? null : index);
    };

    const direction = dir();

    return (
        <section className="faq-section" id="faqs" dir={direction}>
            <div className="container faq-inner">
                <div className="faq-eyebrow">
                    <span className="faq-eyebrow-line" />
                    <span className="faq-eyebrow-text">{t('faqs.eyebrow', { defaultValue: 'FAQ' })}</span>
                </div>

                <h2 className="faq-heading">{t('faqs.title')}</h2>

                {loading ? (
                    <p className="faq-status">{t('faqs.loading', { defaultValue: 'Loading questions…' })}</p>
                ) : null}

                {!loading && error ? (
                    <p className="faq-status faq-status--error">
                        {t('faqs.error', { defaultValue: 'We could not load FAQs. Please try again later.' })}
                    </p>
                ) : null}

                {!loading && !error && faqs.length === 0 ? (
                    <p className="faq-status">{t('faqs.empty', { defaultValue: 'No FAQs have been added yet.' })}</p>
                ) : null}

                {!loading && !error && faqs.length > 0 ? (
                    <ul className="faq-list">
                        {faqs.map((faq, index) => {
                            const isOpen = activeIndex === index;
                            const num = String(index + 1).padStart(2, '0');
                            const question = pickLocalized(faq.question, languageKey);
                            const answer = pickLocalized(faq.answer, languageKey);

                            return (
                                <li key={faq._id || faq.id || index} className={`faq-item${isOpen ? ' is-open' : ''}`}>
                                    <button
                                        className="faq-trigger"
                                        onClick={() => toggleItem(index)}
                                        aria-expanded={isOpen}
                                        aria-controls={`faq-body-${index}`}
                                    >
                                        <span className="faq-number" aria-hidden="true">{num}</span>
                                        <span className="faq-question">{question}</span>
                                        <span className="faq-icon" aria-hidden="true">
                                            <PlusIcon />
                                        </span>
                                    </button>

                                    <div
                                        className="faq-body"
                                        id={`faq-body-${index}`}
                                        role="region"
                                        aria-labelledby={`faq-trigger-${index}`}
                                    >
                                        <div className="faq-body-inner">
                                            <p className="faq-answer">{answer}</p>
                                        </div>
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                ) : null}
            </div>
        </section>
    );
};

export default Questions;
